/**
 * Strategy doc section composer (#936 D4).
 *
 * Source of truth = `Strategy Sections` rows for the active company's Active
 * StrategyDocument. The legacy `StrategyDocument.Body` blob is now a render
 * cache that the agent re-composes after every section write.
 *
 * Layout: section cards grouped by SectionType — Foundations on top, Plan
 * below. Each card renders Title, Markdown body, Policy badge, ConfidenceLevel
 * pill (Foundations only), AutonomousBounds metadata (Plan + autonomous
 * policy). Inline edit + Diff history buttons per card.
 *
 * Header includes an `[Ask the agent (N)]` stub button (#936 D7) — opens a
 * placeholder modal explaining the queued-question infrastructure ships in
 * #951.
 */
import { Component, Input, ChangeDetectorRef, SecurityContext, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { marked } from 'marked';
import {
    marketingStrategyDocumentEntity,
    marketingStrategySectionEntity,
} from 'mj_generatedentities';
import { groupSectionsByType, type SectionGroup, type StrategySectionRow } from './strategy-section-helpers';

@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-doc',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Strategy doc' + (companyName ? ' · ' + companyName : '') + (document ? ' · ' + document.Status : '')"
    [title]="document?.Title || 'Marketing strategy'"
    [sub]="subText"
    [hasActions]="true">
    <ng-container actions>
        <button type="button" class="sd2__ask" (click)="onToggleArtifactView()" *ngIf="document && totalSections > 0" [class.sd2__ask--active]="viewMode === 'artifact'" title="Toggle between section cards and a single rendered document">
            <i class="fa-solid" [class.fa-file-lines]="viewMode === 'cards'" [class.fa-th-list]="viewMode === 'artifact'"></i>
            {{ viewMode === 'cards' ? 'View as document' : 'View as sections' }}
        </button>
        <button type="button" class="sd2__ask" (click)="onAskAgent()">
            <i class="fa-solid fa-message"></i>
            Ask the agent ({{ pendingAgentQuestionCount }})
        </button>
    </ng-container>
</mkt-ws-page-head>

<div *ngIf="askAgentOpen" class="sd2__overlay" (click)="askAgentOpen = false">
    <div class="sd2__ask-modal" (click)="$event.stopPropagation()">
        <h3>CMO Support Agent</h3>
        <p>The CMO Support Agent fab isn't built yet — see <a href="https://github.com/BlueCypress/CDP/issues/951" target="_blank" rel="noopener">#951</a> for the full surface.</p>
        <p class="sd2__sub">Pending questions count is a placeholder until the queued-question infrastructure lands.</p>
        <button type="button" class="sd2__btn" (click)="askAgentOpen = false">Close</button>
    </div>
</div>

<div *ngIf="loading" class="sd2__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading strategy…</div>

<!--
    #938 / #937 — Empty state CTA. When no Active/Draft StrategyDocument
    exists yet, OR the doc exists but has zero parsed sections, the happy-path
    "start" button is the onboarding diagnostic (#937), which extracts
    Foundations from website + blog corpus + LinkedIn signal end-to-end.
    The secondary CTA is for advanced users who already wrote a Body blob
    by hand — explains the parse-script flow without trying to run it from
    the UI (the script lives in canonical, not the browser).
-->
<div *ngIf="!loading && (!document || totalSections === 0)" class="sd2__empty">
    <i class="fa-solid fa-file-circle-question sd2__empty-icon"></i>
    <h2 class="sd2__empty-title">No strategy doc yet.</h2>
    <p class="sd2__empty-sub">
        Run the onboarding diagnostic to extract your Foundations from your
        website, blog corpus, and LinkedIn signal.
    </p>
    <div class="sd2__empty-actions">
        <button type="button" class="sd2__empty-btn sd2__empty-btn--primary"
                (click)="goToDiagnostic()">
            <i class="fa-solid fa-microscope"></i>
            Run onboarding diagnostic
            <i class="fa-solid fa-arrow-right"></i>
        </button>
        <button type="button" class="sd2__empty-btn"
                (click)="parseBodyOpen = true"
                *ngIf="document && totalSections === 0">
            <i class="fa-solid fa-file-code"></i>
            Parse existing Body manually
        </button>
    </div>
    <p *ngIf="document && totalSections === 0" class="sd2__empty-hint">
        This document has a legacy <code>Body</code> blob but no parsed sections yet.
    </p>
</div>

<div *ngIf="parseBodyOpen" class="sd2__overlay" (click)="parseBodyOpen = false">
    <div class="sd2__ask-modal" (click)="$event.stopPropagation()">
        <h3>Parse legacy Body blob</h3>
        <p>
            The <code>StrategyDocument.Body</code> blob is a single Markdown
            string. We split it into <code>StrategySection</code> rows so the
            composer can render each section as a card with its own policy /
            confidence / diff history.
        </p>
        <p>
            Run from a developer shell at the repo root:
        </p>
        <pre class="sd2__codeblock">npm run env:stage -- node packages/Marketing/scripts/parse-strategy-body-to-sections.mjs --companyId={{ companyId }}</pre>
        <p class="sd2__sub">
            One-shot operation. Once it lands, refresh this page and the cards will
            render. The diagnostic flow above is the happy path — use this only
            if you've already pasted a hand-written strategy into the Body field.
        </p>
        <button type="button" class="sd2__btn" (click)="parseBodyOpen = false">Close</button>
    </div>
</div>

<div *ngIf="!loading && document && totalSections > 0 && viewMode === 'cards'" class="sd2__layout">
    <div *ngFor="let group of groups" class="sd2__group">
        <div *ngIf="group.sections.length > 0" class="sd2__group-head">
            <span class="sd2__caps">{{ group.label }}</span>
            <span class="sd2__count">{{ group.sections.length }} sections</span>
        </div>
        <div *ngIf="group.sections.length === 0" class="sd2__group-empty">
            <span class="sd2__caps">{{ group.label }}</span>
            <span>No {{ group.label.toLowerCase() }} sections yet.</span>
        </div>
        <mkt-ws-strategy-section-card
            *ngFor="let s of group.sections"
            [section]="s"
            [canEdit]="canEdit"
            (viewDiffsRequested)="onViewDiffs(s)">
        </mkt-ws-strategy-section-card>
    </div>
</div>

<div *ngIf="!loading && document && totalSections > 0 && viewMode === 'artifact'" class="sd2__artifact">
    <div *ngIf="artifactLoading" class="sd2__loading"><i class="fa-solid fa-spinner fa-spin"></i> Rendering strategy document…</div>
    <div *ngIf="artifactError" class="sd2__artifact-error"><i class="fa-solid fa-triangle-exclamation"></i> {{ artifactError }}</div>
    <div *ngIf="artifactHtml && !artifactLoading" class="sd2__artifact-body" [innerHTML]="artifactHtml"></div>
    <div *ngIf="artifactMarkdown && !artifactLoading" class="sd2__artifact-foot">
        <button type="button" class="sd2__btn" (click)="onCopyArtifact()">
            <i class="fa-solid fa-copy"></i> Copy markdown
        </button>
        <span class="sd2__sub" *ngIf="artifactCopied">copied</span>
    </div>
</div>

<mkt-ws-strategy-section-diff-modal
    [open]="diffModalOpen"
    [sectionId]="activeDiffSectionId"
    [sectionTitle]="activeDiffSectionTitle"
    [canEdit]="canEdit"
    (close)="diffModalOpen = false">
</mkt-ws-strategy-section-diff-modal>
    `,
    styles: [`
        :host { display: block; }
        .sd2__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .sd2__diag { padding: 40px 20px; }

        .sd2__ask { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); cursor: pointer; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); margin-right: 6px; }
        .sd2__ask:hover { background: var(--mj-bg-page, #f5f5f5); }
        .sd2__ask--active { background: color-mix(in srgb, var(--mj-brand-primary, #6366f1) 12%, var(--mj-bg-surface, #fff)); border-color: var(--mj-brand-primary, #6366f1); color: var(--mj-brand-primary, #6366f1); }

        .sd2__artifact { max-width: 920px; padding: 24px 28px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 12px; }
        .sd2__artifact-error { display: flex; align-items: center; gap: 8px; padding: 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #b91c1c; font-size: 13px; }
        .sd2__artifact-body { color: var(--mj-text-primary, #1a1a1a); line-height: 1.65; font-size: 14px; }
        .sd2__artifact-body h1 { font-size: 24px; margin: 0 0 12px; padding-bottom: 12px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .sd2__artifact-body h2 { font-size: 18px; font-weight: 700; margin: 28px 0 12px; }
        .sd2__artifact-body h3 { font-size: 15px; font-weight: 600; margin: 22px 0 8px; color: var(--mj-text-primary, #1a1a1a); }
        .sd2__artifact-body p { margin: 8px 0; }
        .sd2__artifact-body ul, .sd2__artifact-body ol { margin: 8px 0; padding-left: 22px; }
        .sd2__artifact-body code { background: var(--mj-bg-page, #f5f5f5); padding: 1px 6px; border-radius: 4px; font-size: 12.5px; }
        .sd2__artifact-body pre { background: var(--mj-bg-page, #f5f5f5); padding: 10px 12px; border-radius: 6px; overflow-x: auto; font-size: 12.5px; }
        .sd2__artifact-body strong { font-weight: 600; }
        .sd2__artifact-body em { font-style: italic; color: var(--mj-text-secondary, #71717a); }
        .sd2__artifact-body hr { border: none; border-top: 1px solid var(--mj-border-default, #e0e0e0); margin: 18px 0; }
        .sd2__artifact-foot { display: flex; align-items: center; gap: 10px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--mj-border-default, #e0e0e0); }

        .sd2__overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .sd2__ask-modal { background: var(--mj-bg-surface, #fff); padding: 24px; border-radius: 12px; max-width: 460px; width: 92vw; }
        .sd2__ask-modal h3 { margin: 0 0 12px; font-size: 18px; font-weight: 600; }
        .sd2__ask-modal p { margin: 0 0 8px; font-size: 14px; line-height: 1.5; }
        .sd2__sub { color: var(--mj-text-secondary, #71717a); font-size: 12.5px; }
        .sd2__btn { margin-top: 12px; padding: 6px 14px; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); cursor: pointer; font-size: 13px; }

        .sd2__layout { display: flex; flex-direction: column; gap: 28px; max-width: 920px; }
        .sd2__group { display: flex; flex-direction: column; }
        .sd2__group-head { display: flex; align-items: center; gap: 12px; padding-bottom: 8px; margin-bottom: 12px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .sd2__group-empty { display: flex; align-items: center; gap: 12px; padding: 12px 0; color: var(--mj-text-secondary, #71717a); font-size: 13px; }
        .sd2__caps { font-size: 11px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); }
        .sd2__count { font-size: 12px; color: var(--mj-text-secondary, #71717a); }

        .sd2__no-sections { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; margin-top: 24px; }
        .sd2__no-sections i { color: #b45309; margin-top: 2px; }

        /* Empty-state CTA panel (#938 / #937 wiring). */
        .sd2__empty { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 48px 24px; max-width: 560px; margin: 24px auto 0; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 12px; }
        .sd2__empty-icon { font-size: 28px; color: var(--mj-text-secondary, #71717a); margin-bottom: 12px; }
        .sd2__empty-title { font-size: 18px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); margin: 0 0 8px; }
        .sd2__empty-sub { font-size: 13.5px; line-height: 1.55; color: var(--mj-text-secondary, #71717a); margin: 0 0 20px; }
        .sd2__empty-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
        .sd2__empty-btn { display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px; font-size: 13px; font-weight: 500; font-family: inherit; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); cursor: pointer; }
        .sd2__empty-btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .sd2__empty-btn--primary { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }
        .sd2__empty-btn--primary:hover { opacity: 0.92; background: var(--mj-brand-primary, #6366f1); }
        .sd2__empty-hint { margin-top: 14px; font-size: 12px; color: var(--mj-text-secondary, #71717a); }

        .sd2__codeblock { background: var(--mj-bg-page, #f5f5f5); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; padding: 8px 10px; font-size: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; overflow-x: auto; white-space: pre; margin: 8px 0; }
    `]
})
/**
 * Role gating note (#938): route is `requiredRole: 'Admin'`, so non-Admins
 * never reach this section. When inline edit affordances are added later,
 * gate them on `userRole === null || ROLE_RANK[userRole] >= ROLE_RANK['Admin']`.
 */
export class StrategyDocSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
            this.reset();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() companyName = '';
    @Input() canEdit = false;
    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);
    private sanitizer = inject(DomSanitizer);

    loading = false;
    document: marketingStrategyDocumentEntity | null = null;
    sections: StrategySectionRow[] = [];
    groups: SectionGroup[] = [];

    askAgentOpen = false;
    pendingAgentQuestionCount = 0; // #936 D7 stub — wires up in #951

    /** Modal that explains the body-parse script for advanced users. */
    parseBodyOpen = false;

    diffModalOpen = false;
    activeDiffSectionId: string | null = null;
    activeDiffSectionTitle = '';

    /** UI toggle: per-section cards vs single rendered markdown artifact. */
    viewMode: 'cards' | 'artifact' = 'cards';
    /** Cached results from the last GenerateStrategyDoc call. */
    artifactMarkdown: string | null = null;
    artifactHtml: SafeHtml | null = null;
    artifactLoading = false;
    artifactError: string | null = null;
    artifactCopied = false;
    /** Cached generated-doc action ID (one lookup per session). */
    private generateStrategyDocActionId: string | null = null;

    get totalSections(): number {
        return this.sections.length;
    }
    get subText(): string {
        if (!this.document) return '';
        return `${this.totalSections} sections · ${this.document.Status}`;
    }

    onAskAgent(): void {
        this.askAgentOpen = true;
    }

    /** Empty-state primary CTA — routes to the onboarding diagnostic (#937). */
    goToDiagnostic(): void {
        this.navigate('onboarding/chat');
    }

    onViewDiffs(s: StrategySectionRow): void {
        this.activeDiffSectionId = s.ID;
        this.activeDiffSectionTitle = s.Title || s.SectionKey;
        this.diffModalOpen = true;
    }

    /** Toggle between per-section cards and a single rendered artifact. */
    onToggleArtifactView(): void {
        this.viewMode = this.viewMode === 'cards' ? 'artifact' : 'cards';
        if (this.viewMode === 'artifact' && !this.artifactMarkdown) {
            void this.loadArtifact();
        }
        this.cdr.detectChanges();
    }

    private async loadArtifact(): Promise<void> {
        if (!this._companyId || !this.document) return;
        this.artifactLoading = true;
        this.artifactError = null;
        this.cdr.detectChanges();

        try {
            const actionId = await this.resolveGenerateStrategyDocActionId();
            if (!actionId) {
                this.artifactError = 'Could not find the "Generate Strategy Doc" action — metadata may not be synced.';
                return;
            }
            const gql = Metadata.Provider as GraphQLDataProvider;
            const result = await gql.ExecuteGQL(
                `mutation RunAction($input: RunActionInput!) {
                    RunAction(input: $input) { Success Message ResultCode ResultData }
                }`,
                {
                    input: {
                        ActionID: actionId,
                        Params: [
                            { Name: 'CompanyID',          Value: this._companyId,    Type: 'Input' },
                            { Name: 'StrategyDocumentID', Value: this.document.ID,   Type: 'Input' },
                        ],
                    },
                },
            );
            const response = result?.RunAction;
            if (!response?.Success) {
                this.artifactError = response?.Message ?? 'Action returned failure.';
                return;
            }
            // ResultData is the filtered output-params collection
            // (ActionResolver.js:257 — `JSON.stringify(CopyScalarsAndArrays(x))`).
            // CopyScalarsAndArrays can emit EITHER a JSON array `[{Name,Type,Value},…]`
            // OR an array-like object with numeric string keys `{"0":{…},"1":{…}}`
            // (the latter observed in production). Normalize both to an array.
            const parsed: unknown = response.ResultData ? JSON.parse(response.ResultData) : null;
            const paramItems: unknown[] = Array.isArray(parsed)
                ? parsed
                : (parsed !== null && typeof parsed === 'object' ? Object.values(parsed) : []);
            const markdownParam = paramItems
                .find(p => p !== null && typeof p === 'object' && (p as Record<string, unknown>)['Name'] === 'Markdown');
            const markdown = markdownParam && typeof markdownParam === 'object'
                ? (markdownParam as Record<string, unknown>)['Value']
                : null;
            if (typeof markdown !== 'string' || markdown.length === 0) {
                this.artifactError = 'Action succeeded but returned no markdown.';
                return;
            }
            this.artifactMarkdown = markdown;
            const html = marked.parse(markdown, { async: false }) as string;
            this.artifactHtml = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyDocSection] loadArtifact failed: ${msg}`);
            this.artifactError = msg;
        } finally {
            this.artifactLoading = false;
            this.cdr.detectChanges();
        }
    }

    async onCopyArtifact(): Promise<void> {
        if (!this.artifactMarkdown) return;
        try {
            await navigator.clipboard.writeText(this.artifactMarkdown);
            this.artifactCopied = true;
            setTimeout(() => { this.artifactCopied = false; this.cdr.detectChanges(); }, 2000);
            this.cdr.detectChanges();
        } catch (err: unknown) {
            LogError(`[StrategyDocSection] copy to clipboard failed: ${err instanceof Error ? err.message : String(err)}`);
        }
    }

    private async resolveGenerateStrategyDocActionId(): Promise<string | null> {
        if (this.generateStrategyDocActionId) return this.generateStrategyDocActionId;
        const rv = new RunView();
        const r = await rv.RunView<{ ID: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name='Generate Strategy Doc'`,
            ResultType: 'simple',
            Fields: ['ID'],
            MaxRows: 1,
        });
        if (r.Success && r.Results.length > 0) {
            this.generateStrategyDocActionId = r.Results[0].ID;
            return this.generateStrategyDocActionId;
        }
        return null;
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const docResult = await rv.RunView<marketingStrategyDocumentEntity>({
                EntityName: 'Strategy Documents',
                ExtraFilter: `CompanyID='${cid}'`,
                MaxRows: 5,
                ResultType: 'simple',
            });
            if (!docResult.Success) {
                LogError(`[StrategyDocSection] doc query failed: ${docResult.ErrorMessage ?? 'unknown'}`);
                this.reset();
                return;
            }
            const rows = (docResult.Results ?? []) as marketingStrategyDocumentEntity[];
            const filtered = rows.filter(r => r.Status === 'Active' || r.Status === 'Draft');
            filtered.sort((a, b) => b.Version - a.Version);
            this.document = filtered.length > 0 ? filtered[0] : null;

            if (this.document) {
                const sectionResult = await rv.RunView<marketingStrategySectionEntity>({
                    EntityName: 'Strategy Sections',
                    ExtraFilter: `StrategyDocumentID='${this.document.ID}'`,
                    OrderBy: 'DisplayOrder ASC',
                    MaxRows: 200,
                    ResultType: 'simple',
                });
                if (sectionResult.Success) {
                    this.sections = (sectionResult.Results ?? []) as StrategySectionRow[];
                    this.groups = groupSectionsByType(this.sections);
                } else {
                    LogError(`[StrategyDocSection] sections query failed: ${sectionResult.ErrorMessage ?? 'unknown'}`);
                    this.sections = [];
                    this.groups = groupSectionsByType([]);
                }
            } else {
                this.sections = [];
                this.groups = [];
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyDocSection] load failed: ${msg}`);
            this.reset();
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private reset(): void {
        this.document = null;
        this.sections = [];
        this.groups = [];
        // Bust artifact cache when section data goes stale.
        this.artifactMarkdown = null;
        this.artifactHtml = null;
        this.artifactError = null;
        this.cdr.detectChanges();
    }
}
